import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Building2, 
  ChevronLeft,
  Upload,
  Trash2,
  Settings,
  Link2,
  Check,
  X,
  Eye,
  EyeOff,
  RefreshCw,
  ShoppingCart,
  MessageSquare,
  CreditCard,
  BookOpen,
  Save,
  Image,
  Users,
  Mail,
  Key,
  Pencil,
  UserCog
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Country options with currency mapping
const COUNTRIES = [
  { value: 'UAE', label: 'United Arab Emirates', currency: 'AED' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia', currency: 'SAR' },
  { value: 'India', label: 'India', currency: 'INR' },
  { value: 'USA', label: 'United States', currency: 'USD' },
  { value: 'UK', label: 'United Kingdom', currency: 'GBP' },
  { value: 'Qatar', label: 'Qatar', currency: 'QAR' },
  { value: 'Kuwait', label: 'Kuwait', currency: 'KWD' },
  { value: 'Bahrain', label: 'Bahrain', currency: 'BHD' },
  { value: 'Oman', label: 'Oman', currency: 'OMR' },
  { value: 'Egypt', label: 'Egypt', currency: 'EGP' },
  { value: 'Pakistan', label: 'Pakistan', currency: 'PKR' },
];

const CURRENCIES = [
  { value: 'AED', label: 'AED - UAE Dirham', symbol: 'د.إ' },
  { value: 'SAR', label: 'SAR - Saudi Riyal', symbol: '﷼' },
  { value: 'INR', label: 'INR - Indian Rupee', symbol: '₹' },
  { value: 'USD', label: 'USD - US Dollar', symbol: '$' },
  { value: 'GBP', label: 'GBP - British Pound', symbol: '£' },
  { value: 'EUR', label: 'EUR - Euro', symbol: '€' },
  { value: 'QAR', label: 'QAR - Qatari Riyal', symbol: '﷼' },
  { value: 'KWD', label: 'KWD - Kuwaiti Dinar', symbol: 'د.ك' },
  { value: 'BHD', label: 'BHD - Bahraini Dinar', symbol: '.د.ب' },
  { value: 'OMR', label: 'OMR - Omani Rial', symbol: '﷼' },
  { value: 'EGP', label: 'EGP - Egyptian Pound', symbol: '£' },
  { value: 'PKR', label: 'PKR - Pakistani Rupee', symbol: '₨' },
];

// Integration configurations
const INTEGRATIONS = [
  {
    key: 'shopify',
    name: 'Shopify',
    icon: ShoppingCart,
    color: 'green',
    description: 'E-commerce platform integration',
    useOAuth: true, // Flag to indicate OAuth flow
    fields: [
      { key: 'shop_name', label: 'Store Domain', type: 'text', placeholder: 'your-store.myshopify.com' },
    ]
  },
  {
    key: 'whatsapp',
    name: 'WhatsApp Business',
    icon: MessageSquare,
    color: 'emerald',
    description: 'WhatsApp Business API integration',
    fields: [
      { key: 'phone_number_id', label: 'Phone Number ID', type: 'text', placeholder: 'Enter phone number ID' },
      { key: 'business_account_id', label: 'Business Account ID', type: 'text', placeholder: 'Enter WABA ID' },
      { key: 'access_token', label: 'Access Token', type: 'password', placeholder: 'Enter access token' },
    ]
  },
  {
    key: 'mamopay',
    name: 'MamoPay',
    icon: CreditCard,
    color: 'purple',
    description: 'Payment gateway integration',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter MamoPay API key' },
      { key: 'webhook_secret', label: 'Webhook Secret', type: 'password', placeholder: 'Enter webhook secret (optional)' },
    ]
  },
  {
    key: 'zoho',
    name: 'Zoho Books',
    icon: BookOpen,
    color: 'blue',
    description: 'Accounting software integration',
    fields: [
      { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'Enter Zoho client ID' },
      { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'Enter client secret' },
      { key: 'refresh_token', label: 'Refresh Token', type: 'password', placeholder: 'Enter refresh token' },
      { key: 'organization_id', label: 'Organization ID', type: 'text', placeholder: 'Enter org ID' },
    ]
  }
];

const SuperAdminCompanySettingsPage = () => {
  const { companyId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [company, setCompany] = useState(null);
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Company edit form state
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    currency: '',
    industry: ''
  });
  
  // Users management state
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  
  // Delete company state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user && !user.is_super_admin) {
      toast.error('Access denied. Super Admin only.');
      navigate('/dashboard');
      return;
    }
    fetchCompanyData();
  }, [user, companyId, navigate]);

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Use super-admin endpoints
      const companyRes = await axios.get(`${API_BASE_URL}/super-admin/companies/${companyId}`, { headers });
      
      setCompany(companyRes.data);
      // Integrations are already included in company data
      setIntegrations(companyRes.data.integrations || {});
    } catch (error) {
      console.error('Failed to fetch company data:', error);
      toast.error('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch company users
  const fetchCompanyUsers = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/super-admin/companies/${companyId}/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanyUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch company users:', error);
      toast.error('Failed to load company users');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Start editing a user
  const startEditingUser = (userData) => {
    setEditingUser(userData);
    setUserForm({
      full_name: userData.full_name || '',
      email: userData.email || '',
      password: '',
      confirmPassword: ''
    });
    setShowUserPassword(false);
  };

  // Save user changes
  const saveUserChanges = async () => {
    if (!editingUser) return;
    
    // Validate
    if (!userForm.email || !userForm.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (userForm.password && userForm.password !== userForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (userForm.password && userForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSavingUser(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        full_name: userForm.full_name,
        email: userForm.email
      };
      
      // Only include password if it's being changed
      if (userForm.password) {
        updateData.password = userForm.password;
      }
      
      await axios.put(
        `${API_BASE_URL}/super-admin/users/${editingUser.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('User updated successfully');
      setEditingUser(null);
      fetchCompanyUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, WebP, or SVG');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_BASE_URL}/super-admin/companies/${companyId}/logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setCompany(prev => ({ ...prev, logo: response.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Logo upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/super-admin/companies/${companyId}/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCompany(prev => ({ ...prev, logo: null }));
      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const startEditingCompany = () => {
    setCompanyForm({
      name: company.name || '',
      email: company.email || '',
      phone: company.phone || '',
      country: company.country || 'UAE',
      currency: company.currency || 'AED',
      industry: company.industry || ''
    });
    setIsEditingCompany(true);
  };

  const saveCompanyDetails = async () => {
    if (!companyForm.name.trim()) {
      toast.error('Company name is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/super-admin/companies/${companyId}`,
        companyForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setCompany(prev => ({ ...prev, ...companyForm }));
      setIsEditingCompany(false);
      toast.success('Company details updated');
    } catch (error) {
      console.error('Failed to update company:', error);
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  // Delete company permanently
  const handleDeleteCompany = async () => {
    if (deleteConfirmText !== company?.name) {
      toast.error('Please type the company name exactly to confirm');
      return;
    }
    
    if (companyId === 'default-wooden-twist') {
      toast.error('Cannot delete the default company');
      return;
    }
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_BASE_URL}/super-admin/companies/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Company deleted successfully');
      setShowDeleteDialog(false);
      
      // Navigate back to companies list
      navigate('/super-admin/companies');
    } catch (error) {
      console.error('Failed to delete company:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-wt-text-secondary">Company not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="company-settings-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/super-admin/companies')}
          className="text-wt-text-secondary hover:text-wt-text-primary"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">{company.name}</h1>
          <p className="text-wt-text-secondary">Company Settings & Integrations</p>
        </div>
      </div>

      <Tabs defaultValue="general" className="space-y-4" onValueChange={(val) => val === 'users' && fetchCompanyUsers()}>
        <TabsList className="bg-wt-surface border border-wt-border">
          <TabsTrigger value="general" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            <Settings className="h-4 w-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            <Users className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="integrations" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            <Link2 className="h-4 w-4 mr-2" />
            Integrations
          </TabsTrigger>
        </TabsList>

        {/* General Settings Tab */}
        <TabsContent value="general" className="space-y-4">
          {/* Logo Section */}
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader>
              <CardTitle className="text-wt-text-primary flex items-center gap-2">
                <Image className="h-5 w-5 text-wt-gold" />
                Company Logo
              </CardTitle>
              <CardDescription className="text-wt-text-secondary">
                Upload your company logo (JPEG, PNG, GIF, WebP, or SVG - max 5MB)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                {/* Logo Preview */}
                <div className="w-24 h-24 rounded-lg bg-wt-bg border-2 border-dashed border-wt-border flex items-center justify-center overflow-hidden">
                  {company.logo ? (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${company.logo}`}
                      alt="Company Logo"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Building2 className="h-10 w-10 text-wt-text-muted" />
                  )}
                </div>
                
                <div className="space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="bg-wt-gold hover:bg-wt-gold-dark text-black"
                  >
                    {uploadingLogo ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {company.logo ? 'Change Logo' : 'Upload Logo'}
                  </Button>
                  
                  {company.logo && (
                    <Button
                      variant="outline"
                      onClick={handleDeleteLogo}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Info */}
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-wt-text-primary">Company Information</CardTitle>
              {!isEditingCompany ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startEditingCompany}
                  className="border-wt-border text-wt-text-primary"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditingCompany(false)}
                    className="border-wt-border text-wt-text-primary"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveCompanyDetails}
                    disabled={saving}
                    className="bg-wt-gold hover:bg-wt-gold-dark text-black"
                  >
                    {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-secondary">Company ID</Label>
                <p className="text-wt-text-primary font-mono text-sm">{company.id}</p>
              </div>
              <div>
                <Label className="text-wt-text-secondary">Slug</Label>
                <p className="text-wt-text-primary">{company.slug}</p>
              </div>
              
              {isEditingCompany ? (
                <>
                  <div>
                    <Label className="text-wt-text-secondary">Company Name *</Label>
                    <Input
                      value={companyForm.name}
                      onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                      className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                      placeholder="Enter company name"
                    />
                  </div>
                  <div>
                    <Label className="text-wt-text-secondary">Industry</Label>
                    <Input
                      value={companyForm.industry}
                      onChange={(e) => setCompanyForm({ ...companyForm, industry: e.target.value })}
                      className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                      placeholder="e.g., Furniture, Electronics"
                    />
                  </div>
                  <div>
                    <Label className="text-wt-text-secondary">Email</Label>
                    <Input
                      type="email"
                      value={companyForm.email}
                      onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                      className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div>
                    <Label className="text-wt-text-secondary">Phone</Label>
                    <Input
                      value={companyForm.phone}
                      onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                      className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                      placeholder="+971 XX XXX XXXX"
                    />
                  </div>
                  <div>
                    <Label className="text-wt-text-secondary">Country</Label>
                    <Select 
                      value={companyForm.country} 
                      onValueChange={(value) => {
                        // Auto-set currency based on country
                        const country = COUNTRIES.find(c => c.value === value);
                        setCompanyForm({ 
                          ...companyForm, 
                          country: value,
                          currency: country?.currency || companyForm.currency 
                        });
                      }}
                    >
                      <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-surface border-wt-border">
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-wt-text-secondary">Currency</Label>
                    <Select 
                      value={companyForm.currency} 
                      onValueChange={(value) => setCompanyForm({ ...companyForm, currency: value })}
                    >
                      <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-surface border-wt-border">
                        {CURRENCIES.map((currency) => (
                          <SelectItem key={currency.value} value={currency.value}>
                            {currency.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-wt-text-secondary">Email</Label>
                    <p className="text-wt-text-primary">{company.email || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-wt-text-secondary">Phone</Label>
                    <p className="text-wt-text-primary">{company.phone || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-wt-text-secondary">Country</Label>
                    <p className="text-wt-text-primary">{company.country}</p>
                  </div>
                  <div>
                    <Label className="text-wt-text-secondary">Currency</Label>
                    <p className="text-wt-text-primary">{company.currency}</p>
                  </div>
                </>
              )}
              
              <div>
                <Label className="text-wt-text-secondary">Status</Label>
                <Badge 
                  className={
                    company.status === 'active' ? 'bg-green-500/10 text-green-400' :
                    company.status === 'suspended' ? 'bg-orange-500/10 text-orange-400' :
                    'bg-gray-500/10 text-gray-400'
                  }
                >
                  {company.status}
                </Badge>
              </div>
              <div>
                <Label className="text-wt-text-secondary">Created</Label>
                <p className="text-wt-text-primary">
                  {new Date(company.created_at).toLocaleDateString()}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          {companyId !== 'default-wooden-twist' && (
            <Card className="bg-wt-surface border-red-500/50">
              <CardHeader>
                <CardTitle className="text-red-500 flex items-center gap-2">
                  <Trash2 className="h-5 w-5" />
                  Danger Zone
                </CardTitle>
                <CardDescription className="text-wt-text-secondary">
                  Irreversible actions that will permanently affect this company
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                  <div>
                    <h4 className="font-semibold text-wt-text-primary">Delete this company</h4>
                    <p className="text-sm text-wt-text-secondary">
                      Permanently delete {company.name} and all associated data including users, employees, orders, messages, etc.
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => setShowDeleteDialog(true)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Company
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader>
              <CardTitle className="text-wt-text-primary flex items-center gap-2">
                <UserCog className="h-5 w-5 text-wt-gold" />
                Company Users
              </CardTitle>
              <CardDescription className="text-wt-text-secondary">
                Manage admin and staff accounts for this company
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-wt-gold" />
                </div>
              ) : companyUsers.length === 0 ? (
                <div className="text-center py-8 text-wt-text-secondary">
                  No users found for this company
                </div>
              ) : (
                <div className="space-y-3">
                  {companyUsers.map((u) => (
                    <div 
                      key={u.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-wt-bg border border-wt-border"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-wt-gold/10 flex items-center justify-center">
                          <span className="text-wt-gold font-semibold">
                            {u.full_name?.[0]?.toUpperCase() || u.email?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-wt-text-primary">{u.full_name || 'Unnamed User'}</p>
                          <p className="text-sm text-wt-text-secondary flex items-center gap-2">
                            <Mail className="h-3 w-3" />
                            {u.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={u.is_admin ? 'bg-purple-500/10 text-purple-400' : 'bg-gray-500/10 text-gray-400'}>
                          {u.is_admin ? 'Admin' : 'Staff'}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEditingUser(u)}
                          className="border-wt-border text-wt-text-primary hover:bg-wt-gold/10"
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {INTEGRATIONS.map((integration) => {
              const config = integrations[integration.key];
              const isConnected = config && Object.keys(config).length > 0;
              const status = config?.status || (isConnected ? 'unknown' : null);
              const lastValidated = config?.last_validated_at;
              const lastError = config?.last_error;
              const Icon = integration.icon;
              
              return (
                <Card 
                  key={integration.key}
                  className={`bg-wt-surface border-wt-border ${
                    status === 'connected' ? 'border-green-500/30' : 
                    status === 'error' ? 'border-red-500/30' : ''
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg bg-${integration.color}-500/10`}>
                          <Icon className={`h-5 w-5 text-${integration.color}-400`} />
                        </div>
                        <div>
                          <h3 className="font-medium text-wt-text-primary">{integration.name}</h3>
                          <p className="text-xs text-wt-text-secondary">{integration.description}</p>
                        </div>
                      </div>
                      
                      <Badge 
                        variant={status === 'connected' ? 'default' : 'secondary'}
                        className={
                          status === 'connected' ? 'bg-green-500/10 text-green-400' :
                          status === 'error' ? 'bg-red-500/10 text-red-400' :
                          isConnected ? 'bg-yellow-500/10 text-yellow-400' :
                          'bg-gray-500/10 text-gray-400'
                        }
                      >
                        {status === 'connected' ? (
                          <>
                            <Check className="h-3 w-3 mr-1" />
                            Connected
                          </>
                        ) : status === 'error' ? (
                          <>
                            <X className="h-3 w-3 mr-1" />
                            Error
                          </>
                        ) : isConnected ? 'Not Validated' : 'Not Connected'}
                      </Badge>
                    </div>
                    
                    {/* Status details */}
                    {(lastValidated || lastError) && (
                      <div className="mt-2 p-2 rounded bg-wt-bg text-xs">
                        {lastError && (
                          <p className="text-red-400">Error: {lastError}</p>
                        )}
                        {lastValidated && (
                          <p className="text-wt-text-muted">
                            Last checked: {new Date(lastValidated).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Show connected details (read-only) */}
                    {isConnected && config && (
                      <div className="mt-3 p-2 rounded bg-wt-bg text-xs space-y-1">
                        {integration.key === 'shopify' && config.shop_name && (
                          <p className="text-wt-text-secondary">
                            <span className="text-wt-text-muted">Store:</span> {config.shop_name}
                          </p>
                        )}
                        {integration.key === 'whatsapp' && config.phone_number_id && (
                          <p className="text-wt-text-secondary">
                            <span className="text-wt-text-muted">Phone ID:</span> {config.phone_number_id}
                          </p>
                        )}
                        {integration.key === 'zoho' && config.organization_id && (
                          <p className="text-wt-text-secondary">
                            <span className="text-wt-text-muted">Org ID:</span> {config.organization_id}
                          </p>
                        )}
                        {integration.key === 'mamopay' && config.api_key && (
                          <p className="text-wt-text-secondary">
                            <span className="text-wt-text-muted">API Key:</span> ••••{config.api_key.slice(-4)}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Info message */}
                    <div className="mt-4 p-2 rounded bg-blue-500/10 border border-blue-500/20">
                      <p className="text-xs text-blue-400">
                        {isConnected 
                          ? 'This integration was configured by the company from their Integration Hub.'
                          : 'Company can configure this from their Integration Hub page.'
                        }
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <UserCog className="h-5 w-5 text-wt-gold" />
              Edit User
            </DialogTitle>
            <DialogDescription className="text-wt-text-secondary">
              Update user credentials and information
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-wt-text-secondary">Full Name</Label>
              <Input
                value={userForm.full_name}
                onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                placeholder="Enter full name"
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
              />
            </div>
            
            <div>
              <Label className="text-wt-text-secondary">Email Address *</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
                <Input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder="Enter email address"
                  className="bg-wt-bg border-wt-border text-wt-text-primary pl-10"
                />
              </div>
            </div>
            
            <div className="border-t border-wt-border pt-4">
              <Label className="text-wt-text-secondary flex items-center gap-2">
                <Key className="h-4 w-4" />
                Change Password (Optional)
              </Label>
              <p className="text-xs text-wt-text-muted mb-2">Leave blank to keep existing password</p>
              
              <div className="relative mt-1">
                <Input
                  type={showUserPassword ? 'text' : 'password'}
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="New password (min 6 characters)"
                  className="bg-wt-bg border-wt-border text-wt-text-primary pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowUserPassword(!showUserPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-wt-text-muted hover:text-wt-text-primary"
                >
                  {showUserPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              {userForm.password && (
                <div className="mt-2">
                  <Input
                    type={showUserPassword ? 'text' : 'password'}
                    value={userForm.confirmPassword}
                    onChange={(e) => setUserForm({ ...userForm, confirmPassword: e.target.value })}
                    placeholder="Confirm new password"
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                  {userForm.confirmPassword && userForm.password !== userForm.confirmPassword && (
                    <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setEditingUser(null)}
              className="border-wt-border text-wt-text-primary"
            >
              Cancel
            </Button>
            <Button
              onClick={saveUserChanges}
              disabled={savingUser || (userForm.password && userForm.password !== userForm.confirmPassword)}
              className="bg-wt-gold hover:bg-wt-gold-dark text-black"
            >
              {savingUser ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Company Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-wt-surface border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Company Permanently
            </DialogTitle>
            <DialogDescription className="text-wt-text-secondary">
              This action cannot be undone. This will permanently delete:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>All company users and admins</li>
                <li>All employees and attendance records</li>
                <li>All orders and customers</li>
                <li>All inbox messages and conversations</li>
                <li>All products and inventory</li>
                <li>All integrations and sync history</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
              <p className="text-sm text-wt-text-primary">
                To confirm, type <strong className="text-red-500">{company?.name}</strong> below:
              </p>
            </div>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type company name to confirm"
              className="bg-wt-paper border-wt-border text-wt-text-primary"
            />
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteConfirmText('');
              }}
              className="text-wt-text-primary"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCompany}
              disabled={deleteConfirmText !== company?.name || deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminCompanySettingsPage;
